import { uploadDocDetailsNew } from '../../../../../redux/actions/individual-user-flow/individual-user-flow-action';
import { EU_REGION, INDIVIDUAL_POA, INDIVIDUAL_POI } from '../../../../../utils/constants';
import { FLOW_TYPE_ONBOARDING } from '../../../../../utils/constants/eventsConstants';
import { processSubmitPayload } from '../../../../../utils/document-upload/document-upload';
import { LOG_TYPE } from '../../../../../utils/events/eventsConstants';
import { logEvent } from '../../../../../utils/events/logUserEvents';
import moment from 'moment';
import { DocumentObject } from '../../../../RFI/singpass-ekyc/types';

export const getFilteredDocList = (
  docList: any[],
  countryCode?: string,
  nationality?: string,
  isSgKycRfi?: boolean
): any => {
  docList = docList?.sort((item1, item2) => item1?.rendering_sequence - item2?.rendering_sequence);
  const filteredDocList = docList
    ?.filter((item: DocumentObject) => item.type === (isSgKycRfi ? INDIVIDUAL_POA : INDIVIDUAL_POI))
    ?.map((item: DocumentObject) => {
      return {
        fields: item.fields_json,
        isBackRequired: item.is_back_required,
        label: item.label,
        showUploadButton: item.show_upload_button,
        showCaptureButton: item.show_capture_button,
        docInputType: item.document_input_type,
        documentCode: item.document_code
      };
    });
  /*
    for country in EU Region and  nationality LT - BE response is tweaked.
    Hence, capturing PERSONAL_IDENTIFICATION_NUMBER field from one obj and putting it in every other object.
    Removing the PERSONAL_IDENTIFICATION_NUMBER itself later.
  */
  if (EU_REGION.includes(countryCode || '') && nationality === 'LT') {
    try {
      // Find the object with document_code "PERSONAL_IDENTIFICATION_NUMBER"
      const personalIdObject = filteredDocList?.find(
        (obj) => obj.documentCode === 'PERSONAL_IDENTIFICATION_NUMBER'
      );
      // Extract field_json objects from the personalIdObject
      let personalIdNoField = personalIdObject?.fields[0];
      personalIdNoField = {
        ...personalIdNoField,
        name: 'personal_identity_number'
      };

      if (filteredDocList?.length === 1) {
        //Only PIN Field for LT then - EKYC FLOW
        filteredDocList[0] = {
          ...filteredDocList[0],
          fields: [personalIdNoField]
        };
        return [filteredDocList[0]];
      }

      const updatedFilteredDocList = filteredDocList
        ?.map((obj) => {
          if (obj.documentCode !== 'PERSONAL_IDENTIFICATION_NUMBER') {
            return {
              ...obj,
              fields: [personalIdNoField, ...obj.fields]
            };
          }
          return obj;
        })
        ?.filter((obj) => obj.documentCode !== 'PERSONAL_IDENTIFICATION_NUMBER');

      return updatedFilteredDocList;
    } catch (error) {
      return [];
    }
  }
  return filteredDocList;
};

export const getFilteredDocListUS = (docList: any[]): any[] => {
  // Step 1: Group items by 'sequence'
  const groupedBySequence = docList.reduce((acc: any, item: any) => {
    acc[item.sequence] = acc[item.sequence] || [];
    acc[item.sequence].push(item);
    return acc;
  }, {});
  // Step 2: Sort each group by 'rendering_sequence'
  Object.keys(groupedBySequence).forEach((sequence) => {
    groupedBySequence[sequence].sort(
      (a: any, b: any) => a.rendering_sequence - b.rendering_sequence
    );
  });

  return Object.values(groupedBySequence);
};

export const makeSecondCharLowerCase = (input: string): string => {
  if (input?.length >= 2) return input.slice(0, 1) + input[1].toLowerCase() + input.slice(2);
  else return input;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const processPayload = (obj: any, dateFormat = 'DD/MM/YYYY'): any => {
  if (typeof obj === 'object') {
    if ('date_of_expiry' in obj) {
      obj['date_of_expiry'] = moment(obj['date_of_expiry']).format(dateFormat);
    }
    for (const [key, value] of Object.entries(obj)) {
      obj[key] = processPayload(value, dateFormat);
    }
  }
  return obj;
};

export const handleSubmitDocs = async (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  dispatch: any,
  documentCodeSelected: string,
  FORM_ID: string,
  getAllformVal: Record<string, unknown>,
  files: FileList,
  frontFiles: FileList,
  backFiles: FileList,
  countryCode: string,
  nationality: string,
  onfidoCheckCreated: boolean
): Promise<boolean> => {
  const {
    identity_number,
    date_of_expiry,
    issuing_country,
    personal_identity_number,
    type_of_permit,
    date_of_issue
  } = getAllformVal;
  let innerPayload;
  // frontFiles?.[0] have value if its a uploaded img
  //frontFiles directly have value if its a captured img
  const frontDoc = frontFiles?.[0] || frontFiles;
  const backDoc = backFiles?.[0] || backFiles;
  /* Creating payload on the basis of selected doc type for MY*/
  switch (documentCodeSelected) {
    case 'MYPR':
      innerPayload = {
        MYPR: {
          identity_number,
          individual_poi_mypr_doc_file: frontDoc,
          individual_poi_mypr_doc_file_back: backDoc
        }
      };
      break;
    case 'MYKAD':
      innerPayload = {
        MYKAD: {
          identity_number,
          individual_poi_mykad_doc_file: frontDoc,
          individual_poi_mykad_doc_file_back: backDoc
        }
      };
      break;
    case 'DRIVING_LICENSE':
      innerPayload = {
        DRIVING_LICENSE: {
          identity_number,
          date_of_expiry,
          individual_poi_driving_license_doc_file: frontDoc,
          individual_poi_driving_license_doc_file_back: backDoc
        }
      };
      break;
    case 'NATIONAL_ID':
      innerPayload = {
        NATIONAL_ID: {
          identity_number,
          date_of_expiry,
          individual_poi_national_id_doc_file: frontDoc,
          individual_poi_national_id_doc_file_back: backDoc
        }
      };
      break;
    case 'SSN_CARD':
      innerPayload = {
        SSN_CARD: {
          identity_number,
          date_of_expiry,
          SSN_CARD: frontDoc
        }
      };
      break;
    case 'SSN_W2_FORM':
      innerPayload = {
        SSN_W2_FORM: {
          identity_number,
          date_of_expiry,
          SSN_W2_FORM: frontDoc
        }
      };
      break;
    case 'RESIDENCE_PERMIT_ID':
      innerPayload = {
        RESIDENCE_PERMIT_ID: {
          identity_number,
          type_of_permit,
          date_of_expiry,
          date_of_issue,
          issuing_country,
          individual_poi_residence_permit_id_file: frontDoc,
          individual_poi_residence_permit_id_file_back: backDoc
        }
      };
      break;
    default:
      if (nationality === 'LT' && onfidoCheckCreated) break;
      innerPayload = {
        PASSPORT: {
          date_of_expiry,
          identity_number,
          individual_poi_passport_doc_file: files[0],
          issuing_country
        }
      };
  }

  if (nationality === 'LT' && onfidoCheckCreated) {
    //Already completed onfido for LT national, need only PIN - EKYC FLOW
    innerPayload = {
      PERSONAL_IDENTIFICATION_NUMBER: {
        identity_number: personal_identity_number
      }
    };
  } else {
    if (EU_REGION.includes(countryCode || '') && nationality === 'LT') {
      innerPayload = {
        PERSONAL_IDENTIFICATION_NUMBER: {
          identity_number: personal_identity_number
        },
        ...innerPayload
      };
    }
  }

  let payload = {
    INDIVIDUAL_POI: {
      ...innerPayload
    }
  };
  payload = countryCode === 'US' ? processPayload(payload) : payload;

  // @ts-ignore
  const { documents, files: processedFiles } = processSubmitPayload(payload, [], {});
  const response = await dispatch(
    uploadDocDetailsNew(
      processedFiles,
      documents,
      { formID: FORM_ID, payload, doc_count: files?.length },
      false,
      true
    )
  );
  if (response?.data?.success) {
    return true;
  }
  return false;
};

export const logEvents = (): void => {
  const ctData = {
    pageName: 'IdentityVerificationPage'
  };
  logEvent('idDocUpload', FLOW_TYPE_ONBOARDING, ctData, LOG_TYPE.CLEVER_TAP);
};

export const uploadConstants = {
  header: 'Identity verification',
  subHeader: 'Fill in the ID document details for us to verify your identity.',
  submitCTA: 'Submit',
  frontLabel: 'Upload front',
  backLabel: 'Upload back',
  myPassportHeading: 'Upload passport copy',
  documentUploadInstruction: 'Make sure your details and photo are clearly visible.',
  documentUploadInstructionNationalID:
    'Make sure your details and photo are clearly visible.\nMake sure you upload a valid national ID.',
  passportInfoMsg: 'Open personal details section and capture both pages clearly.',
  viewSample: 'View sample',
  passportSampleHeading: 'Passport',
  passportSampleSubHeading: 'Sample illustration',
  passportSampleFooter:
    'As shown in the illustration here, open personal details section and capture both pages clearly',
  passportSampleOKCTA: 'Got it',
  ssnFormImgHeading: 'Upload Social Security card',
  ssnUploadSubHeading: 'Make sure your details are clearly visible.',
  w2FormImgHeading: 'Upload W2 form',
  w2formUploadSubHeading: 'Make sure your details are clearly visible.',
  documentUploadInstructionDrivingLicense: 'Make sure you upload a valid driver license.'
};

export const CorridorSpecificCMSData: any = (cmsData: any, countryCode: string) => {
  const filteredData = cmsData?.labels?.value?.filter((ele: any) => {
    return ele?.countryCode?.includes(countryCode);
  });
  return filteredData?.length > 0 ? filteredData[0] : null;
};
